import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FilterGroup } from '@vendasta/va-filter2';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import {
  DisplayFilter,
  FilterConfig,
  FilterOption,
  LoopOptions,
  LoopV2FiltersService,
} from './loop-v2-filters.service';
import { map, switchMap } from 'rxjs/operators';
import { PostIdentifier, PostInterface } from '@vendasta/the-loop';
import { IAMService, User } from '@vendasta/iam';
import { SessionService } from '@galaxy/core';
import { MatDialog } from '@angular/material/dialog';
import { SingleSelectionDialogComponent } from './filter-dialogs/single-selection-dialog/single-selection-dialog.component';
import { MultiSelectionDialogComponent } from './filter-dialogs/multi-selection-dialog/multi-selection-dialog.component';
import { DateRangeSelectorComponent, SavedFilter } from '@vendasta/uikit';

interface PostsData {
  loading: boolean;
  posts: PostInterface[];
}

@Component({
  selector: 'sales-common-loop-v2-filters',
  templateUrl: './loop-v2-filters.component.html',
  styleUrls: ['./loop-v2-filters.component.scss'],
  providers: [LoopV2FiltersService],
})
export class LoopV2FiltersComponent implements OnInit, OnDestroy {
  @Input() loopOptions: LoopOptions;

  // These clicked events are emitted when showing the compact loop
  @Output() individualPostClicked = new EventEmitter<PostIdentifier>();
  @Output() viewAllClicked = new EventEmitter<boolean>();

  filter: FilterGroup;
  filterItems: DisplayFilter[] = [];
  savedFilters$: Observable<SavedFilter[]> = this.service.savedFilters$;

  postsData$: Observable<PostsData>;
  hasMore$: Observable<boolean> = this.service.hasMore$;
  resourceFilters$: Observable<FilterConfig[]> = this.service.resourceFilters$;
  hasFilters$: Observable<boolean>;

  user$: Observable<User>;
  channelOptions$: Observable<FilterOption[]> = this.service.channelOptions$;
  currentChannel$: Observable<FilterOption> = combineLatest([
    this.service.channelOptions$,
    this.service.selectedChannel$,
  ]).pipe(map(([channelOptions, selectedChannel]) => channelOptions.find((o) => o.value === selectedChannel)));

  subscriptions: Subscription[] = [];

  constructor(
    private readonly dialog: MatDialog,
    private readonly service: LoopV2FiltersService,
    @Inject('partnerId$') private readonly partnerId$: Observable<string>,
    private readonly iam: IAMService,
    private sessionService: SessionService,
  ) {
    this.postsData$ = combineLatest([this.service.loading$, this.service.posts$]).pipe(
      map(([loading, posts]) => ({
        loading: loading,
        posts: posts,
      })),
    );

    this.hasFilters$ = this.resourceFilters$.pipe(
      map(
        (resourceFilters) =>
          !this.loopOptions?.forcedFilters?.marketIds ||
          !this.loopOptions?.forcedFilters?.userId ||
          resourceFilters.length > 0,
      ),
    );
  }

  ngOnInit(): void {
    this.addOrUpdateFilterItem(
      'date-range-filter',
      'FRONTEND.LOOP.FILTERS.DATE_RANGE.TYPE',
      null,
      this.service.dateRangeFilterDisplay$,
    );

    if (!this.loopOptions?.forcedFilters?.marketIds) {
      this.addOrUpdateFilterItem('market-filter', 'FRONTEND.LOOP.MARKET', null, this.service.marketFilterDisplay$);
    }

    if (!this.loopOptions?.forcedFilters?.userId) {
      this.addOrUpdateFilterItem(
        'salespeople-filter',
        'FRONTEND.LOOP.FILTERS.SALESPEOPLE',
        null,
        this.service.salespeopleFilterDisplay$,
      );

      this.addOrUpdateFilterItem(
        'sales-team-filter',
        'FRONTEND.LOOP.FILTERS.SALES_TEAM',
        null,
        this.service.salesTeamFilterDisplay$,
      );

      this.addOrUpdateFilterItem(
        'digitalagents-filter',
        'FRONTEND.LOOP.FILTERS.TASK_MANAGER_USERS',
        null,
        this.service.digitalagentsFilterDisplay$,
      );
    }

    this.subscriptions.push(
      this.service.resourceFilters$
        .pipe(
          map((resourceFilters) =>
            resourceFilters.forEach((resourceFilter) => {
              this.addOrUpdateFilterItem(
                resourceFilter.id,
                resourceFilter.title,
                null,
                this.service.getFilterDisplayForResourceFilter(resourceFilter.id),
              );
            }),
          ),
        )
        .subscribe(),
    );

    this.user$ = combineLatest([this.partnerId$, this.sessionService.getSessionId()]).pipe(
      switchMap(([partnerId, session]) => {
        return this.iam.getMultiUsers([
          {
            namespacedSession: {
              namespace: partnerId,
              session: session,
            },
          },
        ]);
      }),
      map((userIds) => {
        return userIds[0];
      }),
    );
    this.service.initialize(this.loopOptions);
  }

  public loadMore(): void {
    this.service.nextPage();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  addOrUpdateFilterItem(
    filterId: string,
    filterTypeTranslateKey: string,
    value: string,
    value$?: Observable<string>,
  ): void {
    const foundFilter = this.filterItems.filter((item) => item.filterId === filterId);
    if (foundFilter.length > 0) {
      foundFilter[0].displayName = value;
      this.filterItems[this.filterItems.findIndex((el) => el.filterId === filterId)] = foundFilter[0];
      return;
    }
    this.filterItems.push({
      filterId: filterId,
      filterTypeTranslateKey: filterTypeTranslateKey,
      displayName: value,
      displayName$: value$,
    });
  }

  removeFilterItem(filterId: string): void {
    if (filterId === 'market-filter') {
      this.service.setSelectedMarket(null);
    } else if (filterId === 'salespeople-filter') {
      this.service.setSelectedSalespeopleUserIds([]);
    } else if (filterId === 'sales-team-filter') {
      this.service.setSelectedSalesTeam(null);
    } else if (filterId === 'digitalagents-filter') {
      this.service.setSelectedDigitalAgentsUserIds([]);
    } else {
      this.service.setSelectedResourceFilterValues(filterId, []);
    }
    this.service.listPosts(true);
  }

  selectFilterItem(filterId: string): void {
    if (filterId === 'market-filter') {
      this.openMarketDialog();
    } else if (filterId === 'salespeople-filter') {
      this.openSalespeopleDialog();
    } else if (filterId === 'digitalagents-filter') {
      this.openDigitalAgentsDialog();
    } else if (filterId === 'sales-team-filter') {
      this.openSalesTeamDialog();
    } else if (filterId === 'date-range-filter') {
      this.openDateRangeDialog();
    } else {
      this.openResourceFilterDialog(filterId);
    }
  }

  openMarketDialog(): void {
    const dialogRef = this.dialog.open(SingleSelectionDialogComponent, {
      width: '400px',
      data: {
        selected: this.service.getSelectedMarket(),
        options: this.service.marketOptions$,
        title: 'FRONTEND.LOOP.MARKET',
        placeholder: 'FRONTEND.LOOP.FILTERS.PLACEHOLDERS.MARKET',
      },
      id: 'market-selector',
    });
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((result) => {
        if (typeof result !== 'undefined') {
          this.service.setSelectedMarket(result);
          this.service.listPosts(true);
        }
      }),
    );
  }

  openSalespeopleDialog(): void {
    const dialogRef = this.dialog.open(MultiSelectionDialogComponent, {
      width: '400px',
      data: {
        selected: this.service.getSelectedSalespeopleUserIds(),
        options: this.service.salespeopleOptions$,
        title: 'FRONTEND.LOOP.FILTERS.SALESPEOPLE',
        placeholder: 'FRONTEND.LOOP.FILTERS.PLACEHOLDERS.SALESPEOPLE',
      },
      id: 'salespeople-selector',
    });
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((results) => {
        if (typeof results !== 'undefined') {
          this.service.setSelectedSalespeopleUserIds(results);
          this.service.listPosts(true);
        }
      }),
    );
  }

  openDigitalAgentsDialog(): void {
    const dialogRef = this.dialog.open(MultiSelectionDialogComponent, {
      width: '400px',
      data: {
        selected: this.service.getSelectedDigitalAgentsUserIds(),
        options: this.service.digitalagentsOptions$,
        title: 'FRONTEND.LOOP.FILTERS.TASK_MANAGER_USERS',
        placeholder: 'FRONTEND.LOOP.FILTERS.PLACEHOLDERS.TASK_MANAGER_USERS',
      },
      id: 'digitalagents-selector',
    });
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((results) => {
        if (results) {
          this.service.setSelectedDigitalAgentsUserIds(results);
          this.service.listPosts(true);
        }
      }),
    );
  }

  openSalesTeamDialog(): void {
    const dialogRef = this.dialog.open(SingleSelectionDialogComponent, {
      width: '400px',
      data: {
        selected: this.service.getSelectedSalesTeam(),
        options: this.service.salesTeamOptions$,
        title: 'FRONTEND.LOOP.FILTERS.SALES_TEAM',
        placeholder: 'FRONTEND.LOOP.FILTERS.PLACEHOLDERS.SALES_TEAM',
      },
      id: 'sales-team-selector',
    });
    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((result) => {
        if (typeof result !== 'undefined') {
          this.service.setSelectedSalesTeam(result);
          this.service.listPosts(true);
        }
      }),
    );
  }

  openDateRangeDialog(): void {
    this.dialog.open(DateRangeSelectorComponent, {
      id: 'date-range-selector',
    });
  }

  openResourceFilterDialog(resourceFilterId: string): void {
    const dialogRef$ = this.service.resourceFiltersMap$.pipe(
      map((resourceFilters) => resourceFilters.get(resourceFilterId)),
      map((resourceFilter) =>
        this.dialog.open(MultiSelectionDialogComponent, {
          width: '400px',
          data: {
            selected: this.service.getSelectedresourceFilterValues(resourceFilterId),
            options: of(resourceFilter.options),
            title: resourceFilter.title,
            placeholder: 'FRONTEND.LOOP.FILTERS.PLACEHOLDERS.SELECT_FILTERS',
          },
          id: resourceFilter.id,
        }),
      ),
    );
    this.subscriptions.push(
      dialogRef$.pipe(switchMap((dialogRef) => dialogRef.afterClosed())).subscribe((results) => {
        if (typeof results !== 'undefined') {
          this.service.setSelectedResourceFilterValues(resourceFilterId, results);
          this.service.listPosts(true);
        }
      }),
    );
  }

  selectChannel(channel: FilterOption): void {
    this.service.setSelectedChannel(channel.value);
    this.service.listPosts(true);
  }

  selectFilter(filter: SavedFilter): void {
    this.service.selectFilter(filter);
  }

  saveCurrentFilter(filter: string): void {
    this.service.saveFilter(filter);
  }

  removeSavedFilter(filter: string): void {
    this.service.removeFilter(filter);
  }

  clickedViewAll(): void {
    this.viewAllClicked.emit(true);
  }

  clickedPost(identifier: PostIdentifier): void {
    this.individualPostClicked.emit(identifier);
  }
}
